import { FC, useCallback, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import { DialogWindow } from '../DialogWindow';
import { useErrorReportsMutations } from '../../hooks/useErrorReports';
import NewErrorForm from './NewErrorForm';
import ErrorReportList from './ErrorReportList';
import { useEntities } from '../../hooks/useEntities';
import ErrorReportDetails from './ErrorReportDetails';
import UnsavedChangesDialog from '../UnsavedChangesDialog';
import IconButton from '../core/IconButton';
import { IconHelp, IconPlus } from '@/icons';
import useGuideStore from '@/store/guide';
import usePermissions from '../../hooks/usePermissions';

const messages = defineMessages({
  help: {
    id: 'help',
    defaultMessage: 'Help',
  },
  selectPrompt: {
    id: 'error_report.select_or_create_prompt',
    defaultMessage:
      'Select an error report to view its details, or create a new one.',
  },
  selectPromptViewOnly: {
    id: 'error_report.select_prompt',
    defaultMessage: 'Select an error report to view its details.',
  },
});

interface ErrorReportDialogProps {
  schema: EntitySchema;
  open: boolean;
  onClose: () => void;
  entities: Entity[];
  errorReports: ErrorReport[];
}

const ErrorReportDialog: FC<ErrorReportDialogProps> = ({
  schema,
  open,
  onClose,
  entities,
  errorReports,
}) => {
  const intl = useIntl();
  const { openGuide } = useGuideStore();
  const { hasPermission } = usePermissions();
  const canCreateErrorReports = hasPermission('error-reports:create');
  const {
    remove: { isLoading: removingErrorReportInProgress },
    create: { isLoading: creatingErrorReportInProgress },
  } = useErrorReportsMutations();
  const { lookup: entityLookup } = useEntities();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showSelectConfirmation, setShowSelectConfirmation] = useState<
    ErrorReport | undefined
  >(undefined);
  const [selectedErrorReport, setSelectedErrorReport] = useState<
    ErrorReport | undefined
  >(undefined);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  const onFormDirty = useCallback((dirty: boolean) => {
    setIsFormDirty(dirty);
  }, []);

  // Keep a user-chosen stop across entity-list identity changes (e.g. a
  // background refetch). Only fall back when the selected stop is gone.
  const resolvedSelectedEntityId = useMemo(() => {
    if (!selectedEntityId) {
      return null;
    }
    return entities.some((entity) => entity.id === selectedEntityId)
      ? selectedEntityId
      : entities[0]?.id ?? null;
  }, [entities, selectedEntityId]);

  const availableProps = useMemo(() => {
    const propsWithDestinations = Object.entries(schema.extras)
      .filter(([, { support_urls }]) => support_urls)
      .map(([key]) => key);
    if (propsWithDestinations.length === 0) {
      return undefined;
    }
    return propsWithDestinations.map((pkey) => {
      const prop = schema.definition.propertiesLookup[pkey];
      const group =
        typeof prop.group_id === 'number'
          ? schema.definition.groupsLookup[prop.group_id]
          : undefined;
      return {
        id: pkey,
        name: group ? `${group.name}: ${prop.name}` : prop.name,
      };
    });
  }, [schema]);

  const { stationName, selectedEntityName, singleStopLetter } = useMemo(() => {
    const stationName =
      entities.length > 0
        ? (entities[0].properties['meta.name'] as string)
        : '';
    // When the dialog is scoped to a single stop (rather than the whole
    // station/entity group) the title should name that specific stop.
    const singleStopLetter =
      entities.length === 1 ? entities[0].stop_letter : '';
    let selectedEntityName = '';
    for (const e of entities) {
      const selected = e.id === resolvedSelectedEntityId;
      if (selected) {
        selectedEntityName = e.properties['meta.name'] + ' ' + e.stop_letter;
        break;
      }
    }
    return {
      stationName,
      selectedEntityName,
      singleStopLetter,
    };
  }, [entities, resolvedSelectedEntityId]);

  const onTryClose = useCallback(() => {
    if (isCreatingNew && isFormDirty) {
      setShowCancelConfirmation(true);
      return;
    }
    if (isCreatingNew) {
      setIsCreatingNew(false);
      setSelectedEntityId(null);
    }
    onClose();
  }, [onClose, isCreatingNew, isFormDirty]);

  const onTrySelect = useCallback(
    (errorReport: ErrorReport) => {
      if (isCreatingNew && isFormDirty) {
        setShowSelectConfirmation(errorReport);
        return;
      }
      setIsCreatingNew(false);
      setSelectedEntityId(null);
      setSelectedErrorReport(errorReport);
    },
    [isCreatingNew, isFormDirty],
  );

  const isProcessing = useMemo(() => {
    return creatingErrorReportInProgress || removingErrorReportInProgress;
  }, [creatingErrorReportInProgress, removingErrorReportInProgress]);

  return (
    <>
      <UnsavedChangesDialog
        open={showCancelConfirmation}
        onOk={() => {
          setIsCreatingNew(false);
          setSelectedEntityId(null);
          setSelectedErrorReport(undefined);
          setShowCancelConfirmation(false);
          onClose();
        }}
        onCancel={() => setShowCancelConfirmation(false)}
      />
      <UnsavedChangesDialog
        open={!!showSelectConfirmation}
        onOk={() => {
          setSelectedErrorReport(showSelectConfirmation);
          setShowSelectConfirmation(undefined);
          setIsCreatingNew(false);
          setSelectedEntityId(null);
        }}
        onCancel={() => setShowSelectConfirmation(undefined)}
      />
      <DialogWindow
        open={open}
        onOpenChange={(isOpen) => !isOpen && !isProcessing && onTryClose()}
        title={
          singleStopLetter ? (
            <FormattedMessage
              id="error_report.current_error_reports_for_stop"
              defaultMessage="Current error reports for stop {stopLetter} at {stationName}"
              values={{ stopLetter: singleStopLetter, stationName }}
            />
          ) : (
            <FormattedMessage
              id="error_report.current_error_reports_for"
              defaultMessage="Current error reports for the whole station {stationName}"
              values={{ stationName }}
            />
          )
        }
        headerActions={
          <IconButton
            variant="ghost"
            icon={<IconHelp />}
            aria-label={intl.formatMessage(messages.help)}
            onClick={() => openGuide('issueReports')}
          />
        }
        content={
          <div className="flex h-fit gap-4">
            <div className="flex w-1/2 flex-col gap-2">
              <div className="grow overflow-y-auto">
                <ErrorReportList
                  errorReports={errorReports}
                  schemaDefinition={schema.definition}
                  selectedErrorReport={selectedErrorReport}
                  setSelectedErrorReport={onTrySelect}
                  entityLookup={entityLookup}
                />
              </div>
              {canCreateErrorReports && (
                <Button
                  startIcon={<IconPlus />}
                  variant="filled"
                  className="mt-auto shrink-0"
                  disabled={isProcessing || isCreatingNew}
                  onClick={() => {
                    setSelectedErrorReport(undefined);
                    setSelectedEntityId(entities[0]?.id ?? null);
                    setIsCreatingNew(true);
                  }}
                >
                  <FormattedMessage
                    id="error_report.new_error_report"
                    defaultMessage="New error report"
                  />
                </Button>
              )}
            </div>
            <div className="flex min-h-[400px] grow flex-col rounded bg-workorder-100 p-4">
              {selectedErrorReport ? (
                <ErrorReportDetails
                  errorReport={selectedErrorReport}
                  entityLookup={entityLookup}
                  schemaDefinition={schema.definition}
                  onClear={() => setSelectedErrorReport(undefined)}
                />
              ) : isCreatingNew ? (
                <NewErrorForm
                  selectableEntities={entities}
                  setSelectedEntityId={setSelectedEntityId}
                  selectedEntityId={resolvedSelectedEntityId}
                  schema={schema}
                  onCancel={() => {
                    setIsCreatingNew(false);
                    setSelectedEntityId(null);
                  }}
                  onCreated={(report) => {
                    setSelectedErrorReport(
                      errorReports.find((er) => er.id === report.id) ?? report,
                    );
                    setIsCreatingNew(false);
                    setSelectedEntityId(null);
                  }}
                  onDirty={onFormDirty}
                  availableProps={availableProps}
                  entityName={selectedEntityName}
                />
              ) : (
                <div className="flex grow items-center justify-center text-center text-text-800">
                  <FormattedMessage
                    {...(canCreateErrorReports
                      ? messages.selectPrompt
                      : messages.selectPromptViewOnly)}
                  />
                </div>
              )}
            </div>
          </div>
        }
      />
    </>
  );
};

export default ErrorReportDialog;
